<template>
  <div>
    <card-toolbar icon="group" :title="$tc('partition', 2)">
      <template #actions>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn icon v-on="on" :disabled="loading" @click="loadUsers">
              <v-icon outline>
                autorenew
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $tc("reload") }}</span>
        </v-tooltip>

        <v-tooltip v-if="canCreateUser" bottom>
          <template #activator="{ on }">
            <v-btn icon v-on="on" @click="createUser({ user: null })">
              <v-icon id="UsersManagementPage_addUser" outline>
                person_add
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $tc("new_user") }}</span>
        </v-tooltip>
      </template>
    </card-toolbar>

    <v-card flat class="rounded-lg mb-8" outlined style="background: transparent;">
      <v-row no-gutters class="pa-4">
        <v-text-field
          id="UsersManagementPage_search"
          append-icon="mdi-magnify"
          :label="$tc('search')"
          single-line
          hide-details
          clearable
          @input="inputDebounced"
          @click:clear="clearSearch"
        />
      </v-row>
      <v-data-table
        id="user_data_table"
        style="background: transparent;"
        :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
        :headers="headers"
        :loading="loading"
        :search="search"
        :items="listIDsUsers"
        :page="page"
        mobile-breakpoint=""
        @current-items="currentItems"
        @update:page="setPage"
      >
        <template v-if="listIDsUsers.length" #body="{ items }">
          <tbody v-if="loading">
            <tr>
              <td :colspan="headers.length">
                <v-skeleton-loader
                  class="mx-auto"
                  type="table-tbody"
                />
              </td>
            </tr>
          </tbody>
          <tbody id="users_list" v-else>
            <tr
              v-for="item in items"
              :key="item.id"
              :class="selectedRow === item.name ? 'highlight' : null"
            >
              <td class="ma-0 pa-0">
                <v-list-item>
                  <v-list-item-avatar v-if="usersInfos[listCurrentUsers.indexOf(item.name)]?.acl">
                    <v-tooltip top>
                      <template #activator="{ on }">
                        <v-icon
                          v-on="on"
                          :color="
                            getUserIconColor({
                              acl: usersInfos[
                                listCurrentUsers.indexOf(item.name)
                              ].acl,
                            })
                          "
                        >
                          {{
                            getUserIcon({
                              acl: usersInfos[
                                listCurrentUsers.indexOf(item.name)
                              ].acl,
                              blocked:
                                usersInfos[listCurrentUsers.indexOf(item.name)]
                                  .blocked,
                            })
                          }}
                        </v-icon>
                      </template>
                      <span>{{
                        getUserTypeLabel({
                          acl: usersInfos[listCurrentUsers.indexOf(item.name)]
                            .acl,
                          blocked:
                            usersInfos[listCurrentUsers.indexOf(item.name)]
                              .blocked,
                        })
                      }}</span>
                    </v-tooltip>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title id="user_name_list">
                      {{ item.name }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </td>
              <template v-if="$vuetify.breakpoint.mdAndUp">
                <td v-if="usersInfos[listCurrentUsers.indexOf(item.name)]?.acl">
                  <template
                    v-if="
                      isUserTfaOtp({
                        acl: usersInfos[listCurrentUsers.indexOf(item.name)].acl,
                      })
                    "
                  >
                    <span class="caption gray--text"> OATH </span>
                  </template>
                  <template
                    v-else-if="
                      isUserTfaX509({
                        acl: usersInfos[listCurrentUsers.indexOf(item.name)]
                          .acl,
                      })
                    "
                  >
                    <span>x509</span>
                  </template>
                  <template v-else>
                    &nbsp;
                  </template>
                </td>
                <td v-else />

                <td v-if="usersInfos[listCurrentUsers.indexOf(item.name)]?.acl">
                  <template
                    v-if="
                      isUserMxN({
                        acl: usersInfos[listCurrentUsers.indexOf(item.name)]
                          .acl,
                      })
                    "
                  >
                    <v-icon>check</v-icon>
                  </template>
                  <template v-else>
                    &nbsp;
                  </template>
                </td>
                <td v-else />

                <td v-if="usersInfos[listCurrentUsers.indexOf(item.name)]?.acl">
                  <template
                    v-for="(perm, key) in getSystemPermissions({
                      acl: usersInfos[listCurrentUsers.indexOf(item.name)].acl,
                    })"
                  >
                    <v-tooltip :key="`perm-${key}`" top>
                      <template #activator="{ on }">
                        <v-chip
                          v-on="on"
                          label
                          outlined
                          :color="perm.color"
                          class="mx-1 border-radius-0"
                        >
                          {{ key | upper }}
                        </v-chip>
                      </template>
                      <span>{{ perm.label }}</span>
                    </v-tooltip>
                  </template>
                </td>
                <td v-else />

                <td v-if="usersInfos[listCurrentUsers.indexOf(item.name)]?.acl">
                  {{ usersInfos[listCurrentUsers.indexOf(item.name)].totalObjects }}
                </td>
                <td v-else />

                <td v-if="usersInfos[listCurrentUsers.indexOf(item.name)]?.userTrustees">
                  {{ usersInfos[listCurrentUsers.indexOf(item.name)].userTrustees }}
                </td>
                <td v-else />

                <td v-if="usersInfos[listCurrentUsers.indexOf(item.name)]?.userTrusters">
                  {{ usersInfos[listCurrentUsers.indexOf(item.name)].userTrusters }}
                </td>
                <td v-else />
              </template>
              <td style="min-width: 90px">
                <v-row no-gutters justify="end">
                  <menu-actions
                    :id="`menu_actions_${item.name}`"
                    v-if="canCreateUser || canDeleteUser"
                    :options="actionsList"
                    @open="optionsClick(usersInfos[listCurrentUsers.indexOf(item.name)], item)"
                    @close="closeMenu"
                  />
                </v-row>
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-card>

    <confirm-modal
      :open="showDeleteUserModal"
      :title="`${$tc('remove_user')} ?`"
      :message="$tc('alert_user_delete')"
      :critical="true"
      :submessage="$tc('confirm_user_delete')"
      :submessage-subject="selectedItem"
      @negative="closeDeleteUserModal"
      @positive="deleteUser"
    />
  </div>
</template>

<script>
import MenuActions from '@/components/MenuActions.vue'
import PermissionsMixin from '@/mixins/permissions'
import CardToolbar from '@/components/CardToolbar'
import ConfirmModal from '@/components/ConfirmModal.vue'
import debounce from 'lodash.debounce'

export default {
  components: {
    CardToolbar,
    ConfirmModal,
    MenuActions
  },
  mixins: [PermissionsMixin],
  created () {
    this.loadUsers()

    this.inputDebounced = debounce(event => {
      this.search = event
    }, 500)
  },
  mounted () {
    const currentPage = parseInt(this.$route.query.p || 1)

    // TODO: revisar o retorno para a página da listagem de objetos após voltar da página de edição de objetos

    if (this.page !== currentPage) {
      setTimeout(() => {
        this.page = currentPage
        this.loading = false
      }, 500)
    } else {
      this.loading = false
    }
  },
  beforeUnmount () {
    this.inputDebounced.cancel()
  },
  data () {
    return {
      time: null,
      usersInfos: [],
      showDeleteUserModal: false,
      itemsPerPageOptions: [10, 20, 30, 100],
      fullItems: [],
      selectedItem: null,
      loading: true,
      search: null,
      listCurrentUsers: [],
      actionsList: [],
      selectedRow: null,
      page: 1
    }
  },
  computed: {
    listIDsUsers () {
      return this.fullItems.map(item => ({ name: item }))
    },
    appTheme () {
      return this.$vuetify.theme.dark ? 'dark' : 'light'
    },
    headers () {
      const mobileHeaders = [
        {
          text: this.$t('id'),
          align: 'left',
          sortable: true,
          value: 'name'
        },
        {
          text: this.$tc('action', 2),
          align: 'right',
          sortable: false,
          value: null
        }
      ]
      const allHeaders = [
        {
          text: this.$t('id'),
          align: 'left',
          sortable: true,
          value: 'name'
        },
        {
          text: this.$t('tfa'),
          align: 'left',
          sortable: false,
          value: null
        },
        {
          text: this.$t('mxn'),
          align: 'left',
          sortable: false,
          value: null
        },
        {
          text: this.$tc('system_permission', 2),
          align: 'left',
          sortable: false,
          value: null
        }, {
          text: this.$t('total_objects'),
          align: 'left',
          sortable: false,
          value: null
        }, {
          text: this.$t('trustees'),
          align: 'left',
          sortable: false,
          value: null
        }, {
          text: this.$t('trusters'),
          align: 'left',
          sortable: false,
          value: null
        },
        {
          text: this.$tc('action', 2),
          align: 'right',
          sortable: false,
          value: null
        }
      ]
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
        case 'sm': return mobileHeaders
        case 'md':
        case 'lg':
        case 'xl': return allHeaders
        default: return allHeaders
      }
    }
  },
  methods: {
    setPage (page) {
      this.page = page

      this.$router.push({
        query: { p: page }
      })
    },
    optionsClick (usersInfos, item) {
      this.selectedRow = item.name

      this.actionsList = [
        {
          id: 'edit',
          condition: this.canCreateUser,
          icon: 'edit',
          title: this.$tc('edit'),
          action: () => this.createUser({ user: item.name, acl: usersInfos.acl, blocked: usersInfos.blocked, page: this.page })
        },
        {
          id: 'delete',
          condition: this.canDeleteUser,
          icon: 'delete_outline',
          title: this.$tc('delete'),
          action: () => this.askToDeleteUser({ user: item.name })
        }
      ]
    },
    closeMenu () {
      this.selectedRow = null
    },
    clearSearch () {
      this.search = null
    },
    loadUsers () {
      this.time = performance.now()
      this.usersInfos = []
      this.$store.dispatch('user/getListUsers').then(res => {
        this.fullItems = res
      })
    },
    createUser ({ user, acl, blocked, page }) {
      this.$router.push({ name: 'edit-user', params: { user, color: this.getUserIconColor({ acl }), icon: this.getUserIcon({ blocked }), page } })
    },
    askToDeleteUser ({ user }) {
      this.showDeleteUserModal = true
      this.selectedItem = user
    },
    closeDeleteUserModal () {
      this.showDeleteUserModal = false
      this.selectedItem = null
    },
    deleteUser () {
      this.$store.dispatch('user/deleteUser', { user: this.selectedItem })
        .then(() => {
          this.$notify.success({})
          const index = this.fullItems.indexOf(this.selectedItem)
          if (index > -1) {
            this.fullItems.splice(index, 1)
          }
          this.loadUsers()
          this.showDeleteUserModal = false
          this.selectedItem = null
        })
        .catch(err => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }
        })
    },
    async mountUsersForList (items) {
      items = await Promise.all(
        items.map(async user => {
          if (user.acl) {
            return user
          }
          const acl = await this.$store.dispatch('user/getAclByUser', { user })

          const userPerms = this.$store.getters['auth/getPermissions']

          let blocked = 0
          if (userPerms.canCreateUser) {
            blocked = await this.$store.dispatch('user/userIsBlocked', { user })
          }

          let totalObjects = 0
          const body = { user }
          if (body.user !== null) {
            const objCount = await this.$store.dispatch('user/getUserObjCount', body)
            const valuesList = Object.values(objCount)
            totalObjects = valuesList.reduce((a, b) => a + b, 0)
          }

          const userTrustees = await this.$store.dispatch('user/getListUserTrustees', { user })

          const userTrusters = await this.$store.dispatch('user/getListUserTrusters', { user })

          return { name: user, acl, blocked: blocked === -1, totalObjects, userTrustees: userTrustees.length, userTrusters: userTrusters.length }
        })
      )
      return items
    },
    userHaveSystemPermissions ({ acl }) {
      const sysPerms = this.getSystemPermissions({ acl })

      for (const i in sysPerms) {
        if (sysPerms[i].active === true) return true
      }

      return false
    },
    getUserIcon ({ blocked }) {
      if (!blocked) {
        return 'person'
      } else {
        return 'person_off'
      }
    },
    getUserIconColor ({ acl }) {
      const perm = this.$store.getters['acl/getPermissions']({ acl })
      const sysPerm = this.userHaveSystemPermissions({ acl })
      let iconColor
      if (perm.isOperator) {
        iconColor = 'operator_icon_color'
      } else if (sysPerm) {
        iconColor = 'sysPerm_icon_color'
      } else iconColor = 'default_icon_color'
      const colorRgb = this.$vuetify.theme.themes[this.appTheme][iconColor]
      return colorRgb
    },
    isUserTfaOtp ({ acl }) {
      const perm = this.$store.getters['acl/getPermissions']({ acl })
      return perm.isUserTfaOtp
    },
    isUserTfaX509 ({ acl }) {
      const perm = this.$store.getters['acl/getPermissions']({ acl })
      return perm.isUserTfaX509
    },
    isUserMxN ({ acl }) {
      const perm = this.$store.getters['acl/getPermissions']({ acl })
      return perm.isUserNSAuthorization
    },
    getSystemPermissions ({ acl }) {
      const perm = this.$store.getters['acl/getPermissions']({ acl })
      const u = perm.canCreateAndDeleteUser
      const v = perm.canListUser
      const l = perm.canUserAccessRemoteLog
      const f = perm.canUserUpdateFirmware
      const b = perm.canUserExtractBackup

      return {
        u: {
          active: u,
          color: u ? 'blue' : 'grey',
          label: this.$t('perm.user_create_delete')
        },
        v: {
          active: v,
          color: v ? 'blue' : 'grey',
          label: this.$t('perm.user_list')
        },
        l: {
          active: l,
          color: l ? 'blue' : 'grey',
          label: this.$t('perm.log')
        },
        f: {
          active: f,
          color: f ? 'blue' : 'grey',
          label: this.$t('perm.firmware_update')
        },
        b: {
          active: b,
          color: b ? 'blue' : 'grey',
          label: this.$t('perm.backup_restore')
        }
      }
    },
    getUserTypeLabel ({ acl, blocked }) {
      const perm = this.$store.getters['acl/getPermissions']({ acl })
      if (blocked) return this.$t('user_blocked')
      if (perm.isOperator) return this.$t('operator')
      else if (this.userHaveSystemPermissions({ acl })) {
        return this.$t('have_system_permissions')
      } else {
        return this.$t('normal_user')
      }
    },
    async currentItems (val) {
      const array = []
      this.usersInfos = []
      val.forEach(item => {
        array.push(item.name)
      })
      this.listCurrentUsers = array
      this.usersInfos = await this.mountUsersForList(array)
    }
  }
}
</script>

<style>
.border-radius-0 {
  border-radius: 0 !important;
}
</style>
